import { useState, type ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { ProblemError, hasGrant, usePrincipal } from "@csa/web-foundation";
import { useT } from "../../i18n/index";
import { Icon } from "../../ui/icons";
import { BandDonut, Funnel, Sparkline, TONE_MARKER, type ChartTone } from "./charts";
import { useAdvisorDay, type AdvisorDayScope } from "./use-advisor-day";
import type { AdvisorDay } from "./types";

// BUGÜN — the Günüm ("My Day") dashboard. The advisor's morning command centre over
// the composed /v1/metrics/advisor-day read: capacity, SLA health, portfolio depth,
// renewal pipeline, conversion, and the trust/compliance strip. Calm, chartjunk-free,
// Turkish-first. COMMISSION APPEARS NOWHERE; no forecast. Honest-empty everywhere —
// a zero renders a calm zero-state, never a fabricated number.

/** TR decimal (comma) rendering for the mean/median stats — "1.90" → "1,9". */
function decTr(value: string | number | null): string {
  if (value == null) return "—";
  const num = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(num)) return "—";
  return num.toLocaleString("tr-TR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/** 0..1 rate → an integer percent string. */
function pct(rate: number): string {
  return `%${Math.round(rate * 100)}`;
}

/** The "→" link into a target surface, shared by every widget foot. */
function WidgetLink({ to, label }: { to: string; label: string }) {
  return (
    <NavLink to={to} className="gunum-link">
      {label} <Icon name="arrow-right" size={15} />
    </NavLink>
  );
}

/** A small tabular stat (value + label) used inside the widgets. */
function Stat({ value, label, testId }: { value: ReactNode; label: string; testId?: string }) {
  return (
    <div className="gunum-stat">
      <span className="gunum-stat__value" data-testid={testId}>
        {value}
      </span>
      <span className="gunum-stat__label">{label}</span>
    </div>
  );
}

export function GunumDashboard() {
  const { t } = useT();
  const { me } = usePrincipal();
  // A4 — the backend now accepts ?scope=, so the control the old chip only
  // GESTURED at can exist. `undefined` means "don't ask" → the server resolves
  // the widest grant, which is the previous behaviour and the right default.
  const [scope, setScope] = useState<AdvisorDayScope | undefined>(undefined);
  const { data, isLoading, error, refetch, isFetching } = useAdvisorDay(scope);

  // The CEILING comes from the grant, not from the response. Once the user
  // narrows to own, data.scope === "own" — reading the ceiling off the payload
  // would collapse the toggle after one click and strand a manager in own-view.
  const ceiling: AdvisorDayScope | null = hasGrant(me, "queue:read", "all")
    ? "all"
    : hasGrant(me, "queue:read", "team")
      ? "team"
      : null;
  const shown = scope ?? data?.scope;

  return (
    <section className="surface gunum" data-testid="gunum">
      <header className="surface__head">
        <div>
          <h1 className="surface__title">{t("gunum.heading")}</h1>
          <p className="surface__subtitle">{t("gunum.subtitle")}</p>
        </div>
        {ceiling && (
          // Only rendered for a caller who actually holds team/all. An own-only
          // advisor is never shown a choice they do not have — the server would
          // 403 the wider option anyway, and offering it would be the same lie
          // the dead chip told, just with a click target.
          <div className="surface__actions" role="group" aria-label={t("gunum.scope.group")}>
            {(["own", ceiling] as AdvisorDayScope[]).map((s) => (
              <button
                key={s}
                type="button"
                className={"k-chip gunum-scopetab" + (shown === s ? " is-active" : "")}
                aria-pressed={shown === s}
                data-testid={`gunum-scope-${s}`}
                onClick={() => setScope(s)}
              >
                <Icon name="household" size={14} />
                {t(
                  s === "own"
                    ? "gunum.scope.own"
                    : s === "all"
                      ? "gunum.scope.all"
                      : "gunum.scope.team",
                )}
              </button>
            ))}
          </div>
        )}
      </header>

      {isLoading && (
        <div className="k-loading" data-testid="gunum-loading">
          <div className="k-loading__head">
            <span className="k-loading__spinner" />
            <span className="k-loading__label">{t("gunum.loading")}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="k-note k-note--error" data-testid="gunum-error">
          <div className="k-note__row">
            <span className="k-note__icon k-note__icon--error">
              <Icon name="alert-triangle" size={20} />
            </span>
            <div>
              <p className="k-note__title k-note__title--sm">{t("gunum.error.title")}</p>
              <p role="alert" className="k-note__desc">
                {error instanceof ProblemError ? error.problem.detail : t("gunum.error.detail")}
              </p>
              <p className="k-note__desc gunum-reassure">{t("gunum.error.reassure")}</p>
              <div className="k-note__actions">
                <button
                  type="button"
                  className="k-btn k-btn--soft k-btn--sm"
                  disabled={isFetching}
                  onClick={() => void refetch()}
                  data-testid="gunum-retry"
                >
                  {t("gunum.retry")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {data && <Dashboard day={data} />}
    </section>
  );
}

function Dashboard({ day }: { day: AdvisorDay }) {
  const { t } = useT();
  const { capacity, slaHealth, renewalPipeline, conversion, portfolioDepth, trust } = day;

  // Honest-empty for the whole book: nothing has accrued yet. A single calm banner
  // sits above the (still-rendered, zeroed) widgets — never a fake number.
  const bookEmpty =
    capacity.total === 0 &&
    slaHealth.total === 0 &&
    renewalPipeline.total === 0 &&
    conversion.offersSent === 0 &&
    portfolioDepth.households === 0;

  return (
    <>
      {bookEmpty && (
        <div className="k-empty" data-testid="gunum-empty">
          <div className="k-empty__icon">
            <Icon name="dashboard" size={26} />
          </div>
          <p className="k-empty__title">{t("gunum.empty.title")}</p>
          <p className="k-empty__desc">{t("gunum.empty.desc")}</p>
        </div>
      )}

      <div className="gunum-grid gunum-grid--three">
        <CapacityWidget capacity={capacity} />
        <SlaWidget sla={slaHealth} />
        <DepthWidget depth={portfolioDepth} />
      </div>

      <div className="gunum-grid gunum-grid--two">
        <RenewalWidget pipeline={renewalPipeline} />
        <ConversionWidget conversion={conversion} />
      </div>

      <TrustStrip trust={trust} />
    </>
  );
}

// (1) Kapasite & kuyruk — a band donut + n/N counter + a calm capacity line.
function CapacityWidget({ capacity }: { capacity: AdvisorDay["capacity"] }) {
  const { t } = useT();
  const { high, med, low, total, capacityN, utilization } = capacity;
  const bands: { key: string; tone: ChartTone; label: string; value: number }[] = [
    { key: "high", tone: "amber", label: t("band.HIGH"), value: high },
    { key: "med", tone: "blue", label: t("band.MEDIUM"), value: med },
    { key: "low", tone: "neutral", label: t("band.LOW"), value: low },
  ];
  const tight = capacityN > 0 && utilization >= 1;
  return (
    <div className="k-card gunum-widget" data-testid="w-capacity">
      <WidgetHead icon="tasks" title={t("gunum.cap.title")} />
      {total === 0 ? (
        <ZeroLine text={t("gunum.cap.empty")} />
      ) : (
        <>
          <div className="gunum-donut-wrap">
            <BandDonut
              ariaLabel={`${t("gunum.cap.title")}: ${t("band.HIGH")} ${high}, ${t(
                "band.MEDIUM",
              )} ${med}, ${t("band.LOW")} ${low}`}
              segments={bands}
              centerValue={
                <>
                  {total}
                  {capacityN > 0 ? <span className="gunum-donut__den">/{capacityN}</span> : null}
                </>
              }
              centerCaption={t("gunum.cap.totalLabel")}
              valueTestId="cap-counter"
            />
            <ul className="gunum-legend">
              {bands.map((b) => (
                <li key={b.key} className="gunum-legend__row">
                  <span className={`gunum-marker gunum-marker--${b.tone}`} aria-hidden="true">
                    {TONE_MARKER[b.tone]}
                  </span>
                  <span className="gunum-legend__label">{b.label}</span>
                  <span className="gunum-legend__value">{b.value}</span>
                </li>
              ))}
            </ul>
          </div>
          <p className={`gunum-note ${tight ? "gunum-note--amber" : ""}`}>
            {tight ? t("gunum.cap.tight") : t("gunum.cap.healthy")}
            {capacityN > 0 ? ` · ${pct(utilization)} ${t("gunum.cap.utilization")}` : ""}
          </p>
        </>
      )}
      <WidgetLink to="/queue" label={t("gunum.cap.link")} />
    </div>
  );
}

// (2) SLA sağlığı — on-time/at-risk/breached + a 7-day thin trend with a verbal aria.
function SlaWidget({ sla }: { sla: AdvisorDay["slaHealth"] }) {
  const { t } = useT();
  const { onTime, atRisk, breached, total, trend } = sla;
  const series = trend.map((p) => p.onTime + p.atRisk + p.breached);
  const first = series[0] ?? 0;
  const last = series[series.length - 1] ?? 0;
  const dir =
    last > first
      ? t("gunum.sla.trendUp")
      : last < first
        ? t("gunum.sla.trendDown")
        : t("gunum.sla.trendFlat");
  return (
    <div className="k-card gunum-widget" data-testid="w-sla">
      <WidgetHead icon="performance" title={t("gunum.sla.title")} />
      {total === 0 ? (
        <ZeroLine text={t("gunum.sla.empty")} />
      ) : (
        <>
          <div className="gunum-stats">
            <Stat value={onTime} label={t("gunum.sla.onTime")} testId="sla-ontime" />
            <Stat
              value={<span className={atRisk > 0 ? "gunum-val--amber" : ""}>{atRisk}</span>}
              label={t("gunum.sla.atRisk")}
              testId="sla-atrisk"
            />
            <Stat
              value={<span className={breached > 0 ? "gunum-val--red" : ""}>{breached}</span>}
              label={t("gunum.sla.breached")}
              testId="sla-breached"
            />
          </div>
          <p className="gunum-note">{t("gunum.sla.trend")}</p>
          <Sparkline
            values={series}
            ariaLabel={`${t("gunum.sla.trendAria")} ${dir} (${series.join(", ")}).`}
            testId="sla-spark"
          />
        </>
      )}
      <WidgetLink to="/queue" label={t("gunum.sla.link")} />
    </div>
  );
}

// (3) Portföy derinliği — households · active policies · policies-per-household.
function DepthWidget({ depth }: { depth: AdvisorDay["portfolioDepth"] }) {
  const { t } = useT();
  const { households, activePolicies, policiesPerHousehold: ppl } = depth;
  return (
    <div className="k-card gunum-widget" data-testid="w-depth">
      <WidgetHead icon="coverage" title={t("gunum.depth.title")} />
      {households === 0 ? (
        <ZeroLine text={t("gunum.depth.empty")} />
      ) : (
        <>
          <div className="gunum-stats">
            <Stat value={households} label={t("gunum.depth.households")} testId="depth-hh" />
            <Stat
              value={activePolicies}
              label={t("gunum.depth.activePolicies")}
              testId="depth-active"
            />
            <Stat
              value={decTr(ppl.mean)}
              label={t("gunum.depth.perHousehold")}
              testId="depth-mean"
            />
          </div>
          <p className="gunum-note">
            {t("gunum.depth.median")} {decTr(ppl.median)} · {ppl.min}–{ppl.max}{" "}
            {t("gunum.depth.range")}
          </p>
          <p className="gunum-note gunum-note--muted">{t("gunum.depth.service")}</p>
        </>
      )}
      <WidgetLink to="/households" label={t("gunum.depth.link")} />
    </div>
  );
}

// (4) Yenileme hattı — horizontal funnel T-45 → T-7 → (Süresi geçti) + no-silent-lapse note.
function RenewalWidget({ pipeline }: { pipeline: AdvisorDay["renewalPipeline"] }) {
  const { t } = useT();
  const { t45, t7, expiry, total } = pipeline;
  const rows: { key: string; tone: ChartTone; label: string; value: number }[] = [
    { key: "t45", tone: "neutral", label: t("gunum.renew.t45"), value: t45 },
    { key: "t7", tone: "amber", label: t("gunum.renew.t7"), value: t7 },
    {
      key: "expiry",
      tone: expiry > 0 ? "red" : "neutral",
      label: t("gunum.renew.expiry"),
      value: expiry,
    },
  ];
  return (
    <div className="k-card gunum-widget" data-testid="w-renewal">
      <WidgetHead icon="calendar" title={t("gunum.renew.title")} />
      {total === 0 ? (
        <ZeroLine text={t("gunum.renew.empty")} />
      ) : (
        <>
          <Funnel
            rows={rows}
            ariaLabel={`${t("gunum.renew.title")}: ${t("gunum.renew.t45")} ${t45}, ${t(
              "gunum.renew.t7",
            )} ${t7}, ${t("gunum.renew.expiry")} ${expiry}`}
            testId="renew-funnel"
          />
          <p className={`gunum-note ${expiry > 0 ? "gunum-note--amber" : ""}`}>
            {t("gunum.renew.note")}
          </p>
        </>
      )}
      <WidgetLink to="/renewals" label={t("gunum.renew.link")} />
    </div>
  );
}

// (5) Dönüşüm — realized accept rate + offer mix. NO forecast (a small Stage B note).
function ConversionWidget({ conversion }: { conversion: AdvisorDay["conversion"] }) {
  const { t } = useT();
  const { offersSent, accepted, acceptRate, mix } = conversion;
  const mixRows: { key: string; tone: ChartTone; label: string; value: number }[] = [
    { key: "motor", tone: "blue", label: t("gunum.conv.motor"), value: mix.motor },
    { key: "health", tone: "green", label: t("gunum.conv.health"), value: mix.health },
    { key: "other", tone: "neutral", label: t("gunum.conv.other"), value: mix.other },
  ];
  return (
    <div className="k-card gunum-widget" data-testid="w-conversion">
      <WidgetHead icon="suggestion" title={t("gunum.conv.title")} />
      {offersSent === 0 ? (
        <ZeroLine text={t("gunum.conv.empty")} />
      ) : (
        <>
          <div className="gunum-stats">
            <Stat value={pct(acceptRate)} label={t("gunum.conv.acceptRate")} testId="conv-rate" />
            <Stat
              value={`${accepted}/${offersSent}`}
              label={t("gunum.conv.realized")}
              testId="conv-ratio"
            />
          </div>
          <p className="gunum-note">{t("gunum.conv.mix")}</p>
          <Funnel
            rows={mixRows}
            ariaLabel={`${t("gunum.conv.mix")}: ${t("gunum.conv.motor")} ${mix.motor}, ${t(
              "gunum.conv.health",
            )} ${mix.health}, ${t("gunum.conv.other")} ${mix.other}`}
            testId="conv-mix"
          />
        </>
      )}
      <p className="gunum-note gunum-note--muted" data-testid="conv-stageb">
        {t("gunum.conv.stageB")}
      </p>
      <WidgetLink to="/recommendations" label={t("gunum.conv.link")} />
    </div>
  );
}

// Trust & compliance strip — the no-spam + duty-of-care proof, with the calm framing.
function TrustStrip({ trust }: { trust: AdvisorDay["trust"] }) {
  const { t } = useT();
  const { suppressedItems, careHoldHouseholds } = trust;
  const quiet = suppressedItems === 0 && careHoldHouseholds === 0;
  return (
    <div className="k-card k-card--pad-sm gunum-trust" data-testid="w-trust">
      <div className="gunum-trust__head">
        <span className="k-stat__icon k-stat__icon--amber">
          <Icon name="protection" size={18} />
        </span>
        <span className="gunum-widget__title">{t("gunum.trust.title")}</span>
      </div>
      {quiet ? (
        <p className="gunum-note gunum-note--muted" data-testid="trust-empty">
          {t("gunum.trust.empty")}
        </p>
      ) : (
        <div className="gunum-trust__items">
          <div className="gunum-trust__item">
            <span className="gunum-stat__value" data-testid="trust-suppressed">
              {suppressedItems}
            </span>
            <div>
              <p className="gunum-trust__label">{t("gunum.trust.suppressedLabel")}</p>
              <p className="gunum-note gunum-note--muted">{t("gunum.trust.suppressedNote")}</p>
            </div>
          </div>
          <div className="gunum-trust__item">
            <span className="gunum-stat__value" data-testid="trust-carehold">
              {careHoldHouseholds}
            </span>
            <div>
              <p className="gunum-trust__label">{t("gunum.trust.careHoldLabel")}</p>
              <p className="gunum-note gunum-note--muted">{t("gunum.trust.careHoldNote")}</p>
            </div>
          </div>
        </div>
      )}
      <p className="gunum-trust__framing">{t("gunum.trust.framing")}</p>
      <WidgetLink to="/queue" label={t("gunum.trust.link")} />
    </div>
  );
}

// ── Shared bits ──────────────────────────────────────────────────────────────

function WidgetHead({ icon, title }: { icon: Parameters<typeof Icon>[0]["name"]; title: string }) {
  return (
    <div className="gunum-widget__head">
      <span className="k-stat__icon k-stat__icon--blue">
        <Icon name={icon} size={18} />
      </span>
      <span className="gunum-widget__title">{title}</span>
    </div>
  );
}

/** A calm honest-zero line for an empty section — never a fabricated number. */
function ZeroLine({ text }: { text: string }) {
  return (
    <p className="gunum-note gunum-note--zero" data-testid="zero-line">
      {text}
    </p>
  );
}
